import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useSuspenseQuery } from '@tanstack/react-query'

import { logWarning } from '@/services/errorService'

import { getLocation, type LocationData } from '@/shared/api/location'
import { getTimeSlots } from '@/shared/api/timeSlots'
import { initializeLDClient, cleanupLDClient } from '@/lib/launchDarkly'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch } from '@/store'
import {
	restoreCart,
	setReferral,
	setRwgToken,
	setPendingReorder,
	setDineInAllowDuringOffHours,
} from '@/store/ticketSlice'

type LocationBootstrapResult =
	| { location: null; isReady: false }
	| {
			location: LocationData
			isReady: true
			isStoreClosureActive: boolean
			activeStoreClosureMessage: string | null
	  }

export function useLocationBootstrap(): LocationBootstrapResult {
	const ref = useLocationRef()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [searchParams] = useSearchParams()
	const [isReady, setIsReady] = useState(false)
	const [isStoreClosureActive, setIsStoreClosureActive] = useState(false)
	const [activeStoreClosureMessage, setActiveStoreClosureMessage] = useState<string | null>(null)

	const { data: location } = useSuspenseQuery({
		queryKey: ['location', ref],
		queryFn: () => getLocation(ref),
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	useEffect(() => {
		let cancelled = false

		async function bootstrap() {
			setIsReady(false)
			setIsStoreClosureActive(false)
			setActiveStoreClosureMessage(null)

			if (location.isClosed) {
				if (cancelled) return
				setIsReady(true)
				return
			}

			try {
				await initializeLDClient(location)
			} catch (error) {
				logWarning(
					'[useLocationBootstrap] LaunchDarkly init failed, flags will use defaults',
					{
						locationRef: ref,
						error,
					},
				)
			}
			if (cancelled) return

			if (location.orderTypes.length > 0) {
				try {
					const timeSlotsResp = await getTimeSlots(ref, location.orderTypes[0].ref)
					if (timeSlotsResp.storeClosures.length > 0) {
						setIsStoreClosureActive(true)
						setActiveStoreClosureMessage(timeSlotsResp.storeClosures[0].message)
					}
				} catch (error) {
					logWarning(
						'[useLocationBootstrap] Time-slots fetch failed, skipping dynamic closure check',
						{ locationRef: ref, orderTypeRef: location.orderTypes[0].ref, error },
					)
				}
				if (cancelled) return
			}

			const referralGuid = searchParams.get('referralGuid')
			const referralSource = searchParams.get('referralSource')
			const rwgToken = searchParams.get('rwg_token')
			const itemGuid = searchParams.get('itemGuid')
			const itemRef = searchParams.get('itemRef')

			const paramsToStrip: string[] = []

			if (referralGuid && referralSource) {
				dispatch(setReferral({ guid: referralGuid, source: referralSource }))
				paramsToStrip.push('referralGuid', 'referralSource')
			}

			if (rwgToken) {
				dispatch(setRwgToken(rwgToken))
				paramsToStrip.push('rwg_token')
			}

			if (itemGuid && itemRef) {
				dispatch(setPendingReorder({ itemGuid, itemRef }))
			}

			if (paramsToStrip.length > 0) {
				const next = new URLSearchParams(searchParams)
				paramsToStrip.forEach((p) => next.delete(p))
				const qs = next.toString()
				void navigate(`${pathname}${qs ? `?${qs}` : ''}`, { replace: true })
			}

			if (cancelled) return

			// Store dineInAllowDuringOffHours from location data so the checkout gate
			// (selectIsScheduleAvailable) can apply the off-hours dine-in restriction
			// without requiring CartActionsFooter to have access to location data.
			dispatch(setDineInAllowDuringOffHours(location.dineInAllowDuringOffHours))

			await dispatch(restoreCart({ ref, location }))
			if (cancelled) return

			setIsReady(true)
		}

		void bootstrap()

		return () => {
			cancelled = true
			void cleanupLDClient()
		}
	}, [ref, location]) // eslint-disable-line react-hooks/exhaustive-deps -- dispatch/navigate/searchParams/pathname are stable refs; intentionally excluded

	if (!isReady) return { location: null, isReady: false }
	return { location, isReady: true, isStoreClosureActive, activeStoreClosureMessage }
}
